import axios from 'axios';
import Config from '../../../../config';
import Auth from '../../../../auth';
import { API_ENDPOINTS } from '../constants';
import '../types';

class FinancingService {
  /**
   * Get financing connect token
   * @returns Connect token
   */
  async getConnectToken(): Promise<string> {
    try {
      const response = await axios.post(
        `${Config.api}${API_ENDPOINTS.FINANCING_CONNECT}`
      );
      return response.data.Body;
    } catch (error) {
      console.error('Error getting financing connect token:', error);
      throw error;
    }
  }

  /**
   * Finance selected orders
   * @param orderIds - Order IDs to finance
   * @returns Session token
   */
  async financeOrders(orderIds: Array<string | number>): Promise<string> {
    try {
      const response = await axios.post(
        `${Config.api}${API_ENDPOINTS.FINANCE_ORDERS}?ids=${orderIds.join(',')}`
      );
      return response.data.Body;
    } catch (error) {
      console.error('Error financing orders:', error);
      throw error;
    }
  }

  /**
   * Check if financing should be shown
   * @param financialStatus - Current financial status
   * @returns Whether to show financing options
   */
  shouldShowFinancing(financialStatus: string | null | undefined): boolean {
    return (
      (Auth.hasPermission("settings/manage") && financialStatus === "OFFERED") ||
      financialStatus === "ACCEPTED"
    );
  }

  /**
   * Get button text based on financial status
   * @param financialStatus - Current financial status
   * @returns Button text
   */
  getFinanceButtonText(financialStatus: string | null | undefined): string {
    switch (financialStatus) {
      case "OFFERED":
        return "View financing offers";
      case "ACCEPTED":
        return "View orders to finance";
      default:
        return "Finish financing application";
    }
  }

  /**
   * Get color for financial status
   * @param status - Financial status
   * @returns Color hex code
   */
  getFinancialStatusColor(status: string): string {
    const colors: Record<string, string> = {
      'Paid': '#448044',
      'Overdue': '#EE3224',
      'Not Eligible': '#AAA',
      'Pending': '#FFA500'
    };
    return colors[status] || '#FFA500';
  }

  /**
   * Initialize Kanmon Connect for financing
   * @param connectToken - Connect token
   * @param showDashboard - Whether to show the Kanmon dashboard immediately
   */
  initializeKanmonConnect(connectToken: string, showDashboard = true): void {
    if (window.KANMON_CONNECT) {
      window.KANMON_CONNECT.start({ connectToken });
      if (showDashboard) {
        window.KANMON_CONNECT.show();
      }
    } else {
      console.error('Kanmon Connect not loaded');
      throw new Error('Financing service not available');
    }
  }

  /**
   * Show financing flow for orders
   * @param sessionToken - Session token
   */
  showFinancingFlow(sessionToken: string): void {
    if (window.KANMON_CONNECT) {
      window.KANMON_CONNECT.show({
        sessionToken,
        component: 'INVOICE_FLOW'
      });
    } else {
      console.error('Kanmon Connect not loaded');
      throw new Error('Financing service not available');
    }
  }
}

const financingService = new FinancingService();
export default financingService;